import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Flame, Calendar, Dog, Shield, HelpCircle, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";
import { SectionHeader } from "@/components/ui-kit";
import ShareBar from "@/components/ShareBar";
import { MEDIA } from "@/lib/media";

function useCountdown(target) {
  const [t, setT] = useState({});
  useEffect(() => {
    const tick = () => {
      const diff = new Date(target) - new Date();
      if (diff <= 0) return setT({ d: 0, h: 0, m: 0, s: 0 });
      setT({
        d: Math.floor(diff / 86400000), h: Math.floor((diff / 3600000) % 24),
        m: Math.floor((diff / 60000) % 60), s: Math.floor((diff / 1000) % 60),
      });
    };
    tick(); const id = setInterval(tick, 1000); return () => clearInterval(id);
  }, [target]);
  return t;
}

export default function Event() {
  useSeo("14DDV — 14 Dogs for Disabled Veterans", "14 Dogs for Disabled Veterans. Fourteen dogs, four days, one mission. Labor Day Weekend, September 4–7, 2026 in Chicago.");
  const [ev, setEv] = useState(null);
  const cd = useCountdown("2026-09-04T09:00:00");
  useEffect(() => { api.get("/event").then((r) => setEv(r.data)); }, []);

  const pct = ev ? Math.min(100, Math.round((ev.raised / ev.goal) * 100)) : 0;

  return (
    <div>
      <section className="relative min-h-[70vh] flex items-center grain">
        <img src={MEDIA.dog2} alt="Working dog" className="absolute inset-0 w-full h-full object-cover opacity-35" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/70 to-[#0A0A0A]/40" />
        <div className="relative max-w-7xl mx-auto px-6 py-24 text-center w-full">
          <Flame className="w-10 h-10 text-[#D4AF37] mx-auto mb-4" />
          <h1 className="font-heading text-4xl sm:text-6xl font-bold text-[#FDFBF7]">14DDV — 14 Dogs for Disabled Veterans</h1>
          <p className="mt-4 text-xl text-[#D4AF37] font-heading italic">Fourteen Dogs. Four Days. One Mission.</p>
          <p className="mt-3 text-[#C8C4B7]">Labor Day Weekend · September 4–7, 2026 · Chicago</p>
          <div className="mt-8 flex justify-center gap-3 sm:gap-6">
            {[["Days", cd.d], ["Hrs", cd.h], ["Min", cd.m], ["Sec", cd.s]].map(([l, v]) => (
              <div key={l} className="bg-[#141414]/80 border border-white/10 px-4 sm:px-6 py-4 min-w-[70px]" data-testid={`countdown-${l.toLowerCase()}`}>
                <div className="font-heading text-3xl font-bold text-[#D4AF37]">{v ?? 0}</div>
                <div className="text-xs text-[#8A877B] uppercase tracking-wider">{l}</div>
              </div>
            ))}
          </div>
          <div className="mt-8 flex justify-center gap-3 flex-wrap">
            <a href="#register" className="btn-gold" data-testid="event-register-cta">Register</a>
            <a href="#sponsor" className="btn-outline" data-testid="event-sponsor-cta">Sponsor a Dog</a>
          </div>
        </div>
      </section>

      {/* Thermometer */}
      <section className="bg-[#141414] border-y border-white/10 py-14">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <div className="overline mb-2">Live Fundraising</div>
          <div className="font-heading text-4xl font-bold text-[#FDFBF7]">${(ev?.raised || 0).toLocaleString()} <span className="text-[#8A877B] text-2xl">of ${(ev?.goal || 0).toLocaleString()}</span></div>
          <div className="mt-5 h-4 bg-[#0A0A0A] border border-white/10 overflow-hidden">
            <motion.div initial={{ width: 0 }} whileInView={{ width: `${pct}%` }} viewport={{ once: true }} transition={{ duration: 1.2 }} className="h-full bg-gradient-to-r from-[#4A5D23] to-[#D4AF37]" />
          </div>
          <p className="mt-3 text-sm text-[#8A877B]">{pct}% toward funding all 14 dogs</p>
        </div>
      </section>

      {/* Dogs */}
      <section className="max-w-7xl mx-auto px-6 py-20" id="sponsor">
        <SectionHeader overline="Meet the Pack" title="Fourteen Dogs" sub="Each dog is training toward a life-changing placement with a disabled veteran. Sponsor a dog to fund its journey." />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {(ev?.dogs || []).map((d, i) => (
            <motion.div key={d.id} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 4) * 0.06 }}
              className="gw-card overflow-hidden" data-testid={`dog-${d.number}`}>
              <div className="relative h-40"><img src={i % 2 ? MEDIA.dog1 : MEDIA.dog2} alt={d.name} className="w-full h-full object-cover opacity-80" /><span className="absolute top-2 left-2 bg-[#D4AF37] text-black text-xs font-bold px-2 py-0.5">#{d.number}</span></div>
              <div className="p-4">
                <h3 className="font-heading text-lg font-semibold text-[#FDFBF7]">{d.name}</h3>
                <p className="text-xs text-[#8A877B]">{d.breed}</p>
                <p className="text-xs text-[#C8C4B7] mt-2">{d.goal}</p>
                <p className="text-[10px] text-[#D4AF37] mt-2 uppercase tracking-wider">{d.training_status}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Schedule + FAQ */}
      <section className="bg-[#141414] border-y border-white/10 py-20">
        <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-14">
          <div>
            <div className="overline mb-4 flex items-center gap-2"><Calendar className="w-4 h-4" /> Event Schedule</div>
            <div className="space-y-5">
              {(ev?.schedule || []).map((s) => (
                <div key={s.day} className="border-l-2 border-[#D4AF37]/40 pl-4">
                  <h4 className="font-heading text-lg text-[#FDFBF7]">{s.day}</h4>
                  <ul className="mt-1 space-y-1">{s.items.map((it) => <li key={it} className="text-sm text-[#C8C4B7]">— {it}</li>)}</ul>
                </div>
              ))}
            </div>
          </div>
          <div>
            <div className="overline mb-4 flex items-center gap-2"><HelpCircle className="w-4 h-4" /> FAQ</div>
            <div className="space-y-4">
              {(ev?.faqs || []).map((f) => (
                <div key={f.q} className="gw-card p-5"><h4 className="text-[#FDFBF7] font-semibold">{f.q}</h4><p className="text-sm text-[#C8C4B7] mt-1">{f.a}</p></div>
              ))}
            </div>
            <div className="gw-card p-5 mt-4 flex gap-3"><Shield className="w-6 h-6 text-[#4A5D23] shrink-0" /><p className="text-sm text-[#C8C4B7]">All training follows humane, force-free animal-welfare standards with on-site veterinary support. Weather alerts and schedule changes are sent to all registrants.</p></div>
          </div>
        </div>
      </section>

      {/* Register */}
      <section className="max-w-3xl mx-auto px-6 py-20" id="register">
        <RegisterForm />
      </section>
    </div>
  );
}

function RegisterForm() {
  const [f, setF] = useState({ name: "", email: "", phone: "", category: "spectator", ticket_type: "free", emergency_contact: "", waiver: false });
  const [loading, setLoading] = useState(false);
  const cats = [["spectator", "Attend (Free)"], ["veteran", "Register as Veteran"], ["volunteer", "Volunteer"], ["vendor", "Vendor (Paid)"], ["press", "Press"]];
  const submit = async (e) => {
    e.preventDefault();
    if (!f.waiver) return toast.error("Please accept the digital waiver");
    setLoading(true);
    try { await api.post("/event/register", f); toast.success("You're registered! Check your email."); setF({ ...f, name: "", email: "" }); }
    catch { toast.error("Registration failed"); } finally { setLoading(false); }
  };
  return (
    <div className="gw-card p-8">
      <SectionHeader overline="Join 14DDV" title="Register" sub="Veteran medical or disability information is never collected or displayed publicly." />
      <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2"><label className="block text-sm text-[#C8C4B7] mb-2">Registration type</label>
          <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value, ticket_type: ["vendor"].includes(e.target.value) ? "paid" : "free" })} className="gw-input" data-testid="reg-category">
            {cats.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
        <div><label className="block text-sm text-[#C8C4B7] mb-2">Name *</label><input required className="gw-input" data-testid="reg-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
        <div><label className="block text-sm text-[#C8C4B7] mb-2">Email *</label><input type="email" required className="gw-input" data-testid="reg-email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></div>
        <div><label className="block text-sm text-[#C8C4B7] mb-2">Phone</label><input className="gw-input" data-testid="reg-phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></div>
        <div><label className="block text-sm text-[#C8C4B7] mb-2">Emergency contact</label><input className="gw-input" data-testid="reg-emergency" value={f.emergency_contact} onChange={(e) => setF({ ...f, emergency_contact: e.target.value })} /></div>
        <label className="sm:col-span-2 flex items-start gap-3 cursor-pointer text-sm text-[#C8C4B7]" data-testid="reg-waiver" onClick={() => setF({ ...f, waiver: !f.waiver })}>
          <span className={`w-5 h-5 border shrink-0 flex items-center justify-center mt-0.5 ${f.waiver ? "bg-[#D4AF37] border-[#D4AF37]" : "border-white/30"}`}>{f.waiver && <span className="text-black text-xs">✓</span>}</span>
          I accept the digital participation waiver and animal-safety guidelines.
        </label>
        <button disabled={loading} className="btn-gold sm:col-span-2 flex items-center justify-center gap-2 py-3.5" data-testid="reg-submit">{loading && <Loader2 className="w-4 h-4 animate-spin" />} Complete Registration</button>
      </form>
      <div className="mt-6"><ShareBar title="Join me at 14DDV — 14 Dogs for Disabled Veterans" /></div>
    </div>
  );
}
